import styled from 'styled-components';
import { Typography, Card, Button, Row, Space, Divider } from 'antd';
import { MessageOutlined, ReloadOutlined } from '@ant-design/icons';
import useTranslations from 'hooks/useTranslations';
import useCurrentTherapistBooking from 'hooks/useCurrentTherapistBooking';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import BookingTagStatus from 'components/global/BookingTagStatus';
import { useMemo } from 'react';

const { Text } = Typography;

const BOOKING_STATUS = {
    PENDING: 1,
    COMPLETED: 5,
};

const BOTTOM_BAR_HEIGHT = 61;

const BookingWidgetContainer = styled.div`
    position: fixed;
    bottom: calc(${BOTTOM_BAR_HEIGHT}px + env(safe-area-inset-bottom));
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 430px;
    z-index: 998;
    animation: slideUp 0.3s ease-out;

    @keyframes slideUp {
        from {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
        }
        to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
        }
    }
`;

const BookingCard = styled(Card)`
    background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
    border: none;
    border-bottom-left-radius: unset;
    border-bottom-right-radius: unset;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

const WhiteText = styled(Text)`
    color: white;
`;

const WhiteTextBold = styled(WhiteText)`
    font-weight: bold;
    font-size: 16px;
`;

const WhiteTextSecondary = styled(WhiteText)`
    color: rgba(255, 255, 255, 0.8);
    font-size: 12px;
`;

const WhiteTextMedium = styled(WhiteText)`
    font-size: 14px;
    display: block;
`;

const WhiteButton = styled(Button)`
    color: white;
`;

const ActionButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const TimeDivider = styled(Text)`
    color: white;
`;

const WhiteDivider = styled(Divider)`
    color: white;
    border-color: white;
`;

const formatTime = (timestamp) => moment.unix(timestamp).format('HH:mm A');

const formatTimeRange = (startTime, endTime) => (
    <Space size={4} split={<TimeDivider>-</TimeDivider>}>
        <WhiteText>{formatTime(startTime)}</WhiteText>
        <WhiteText>{formatTime(endTime)}</WhiteText>
    </Space>
);

function TherapistBookingView({ data, onViewDetail, textViewDetail, textOngoingBooking }) {
    return (
        <>
            <HeaderRow>
                <WhiteTextBold>{textOngoingBooking}</WhiteTextBold>
                <WhiteButton type="link" size="small" onClick={onViewDetail}>
                    {textViewDetail}
                </WhiteButton>
            </HeaderRow>
            <Row justify="space-between" align="middle">
                <Space direction="vertical" size={0}>
                    <WhiteTextMedium>{data?.user_address?.place_name}</WhiteTextMedium>
                    <WhiteTextSecondary>{data?.user_address?.address}</WhiteTextSecondary>
                </Space>
                {formatTimeRange(data?.actual_start_at, data?.ctime)}
            </Row>
        </>
    );
}

function GuestBookingView({
    data,
    onViewDetail,
    onOpenChat,
    onLeaveReview,
    onRefresh,
    textCurrentBooking,
    textViewDetail,
    textOpenChat,
    textLeaveReview,
    textRefreshStatus,
}) {
    const showChatButton = data?.status !== BOOKING_STATUS.PENDING;

    const renderActionButton = () => {
        if (data?.status === BOOKING_STATUS.PENDING) {
            return (
                <WhiteButton type="link" icon={<ReloadOutlined />} size="small" onClick={onRefresh}>
                    {textRefreshStatus}
                </WhiteButton>
            );
        }

        if (data?.status === BOOKING_STATUS.COMPLETED) {
            return (
                <Button type="link" size="small" onClick={onLeaveReview}>
                    {textLeaveReview}
                </Button>
            );
        }

        return (
            <Button type="link" onClick={onViewDetail}>
                {textViewDetail}
            </Button>
        );
    };

    return (
        <>
            <HeaderRow>
                <WhiteTextBold>{textCurrentBooking}</WhiteTextBold>
                <BookingTagStatus status={data?.status} />
            </HeaderRow>
            <Row justify="space-between" align="middle">
                <Space direction="vertical" size={0}>
                    <WhiteTextMedium strong>{data?.code}</WhiteTextMedium>
                    <Space split={<WhiteDivider type="vertical" />} size={0}>
                        {formatTimeRange(data?.scheduled_at, data?.expected_end_at)}
                        {showChatButton && (
                            <WhiteButton icon={<MessageOutlined />} type="link" style={{ padding: '4px 0px' }} onClick={onOpenChat}>
                                {textOpenChat}
                            </WhiteButton>
                        )}
                    </Space>
                </Space>
                <ActionButtonContainer>{renderActionButton()}</ActionButtonContainer>
            </Row>
        </>
    );
}

function BookingWidget() {
    const { t } = useTranslations();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const isTherapistMenu = useMemo(() => {
        return pathname?.startsWith('/therapist/');
    }, [pathname]);

    const { data, refetch } = useCurrentTherapistBooking();

    const translations = useMemo(
        () => ({
            refreshStatus: t('RefreshStatus', 'Refresh Status'),
            ongoingBooking: t('OngoingBooking', 'Ongoing Booking'),
            currentBooking: t('CurrentBooking', 'Current Booking'),
            viewDetail: t('ViewDetail', 'View Detail'),
            openChat: t('OpenChat', 'Open Chat'),
            leaveReview: t('LeaveReview', 'Leave Review'),
        }),
        [t]
    );

    const handleTherapistViewDetail = () => {
        navigate(`/therapist/my-booking/detail/${data?.id}`);
    };

    const handleGuestViewDetail = () => {
        navigate(`/mybooking.list/detail/${data?.code}`);
    };

    const handleOpenChat = () => {
        navigate(`/chat/${data?.id}/${data?.code}`);
    };

    const handleLeaveReview = () => {
        navigate(`/service-feedback/${data?.code}`);
    };

    if (!data) {
        return null;
    }

    return (
        <BookingWidgetContainer>
            <BookingCard>
                {isTherapistMenu ? (
                    <TherapistBookingView
                        data={data}
                        onViewDetail={handleTherapistViewDetail}
                        textViewDetail={translations.viewDetail}
                        textOngoingBooking={translations.ongoingBooking}
                    />
                ) : (
                    <GuestBookingView
                        data={data}
                        onViewDetail={handleGuestViewDetail}
                        onOpenChat={handleOpenChat}
                        onLeaveReview={handleLeaveReview}
                        onRefresh={refetch}
                        textCurrentBooking={translations.currentBooking}
                        textViewDetail={translations.viewDetail}
                        textOpenChat={translations.openChat}
                        textLeaveReview={translations.leaveReview}
                        textRefreshStatus={translations.refreshStatus}
                    />
                )}
            </BookingCard>
        </BookingWidgetContainer>
    );
}

export default BookingWidget;
